import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'

interface PendingAction {
  toolName: string
  args: Record<string, unknown>
  description: string
  // The note or the person an update / delete is about (absent from older servers).
  targetLabel?: string
}

interface ChatbotConfirmParams {
  conversationId: string
  actions: { toolName: string; args: Record<string, unknown> }[]
}

interface ChatbotConfirmResponse {
  response: string
  success: boolean
  pendingActions?: PendingAction[]
}

const confirmAction = async (params: ChatbotConfirmParams): Promise<ChatbotConfirmResponse> => {
  const { data } = await api.post<ChatbotConfirmResponse>('/ft_chatbot/confirm-action', params)
  return data
}

export const useFtChatbotConfirm = () => {
  const queryClient = useQueryClient()

  return useMutation<ChatbotConfirmResponse, Error, ChatbotConfirmParams>({
    mutationFn: confirmAction,
    // The lists the confirmed actions concern are refreshed, as after the same
    // change made by hand. Also after an error: the action may have been
    // carried out before the reply failed. A new write tool of the assistant
    // must be added here. 'notes-summary' is left out on purpose: refreshing
    // it would ask the AI again.
    onSettled: (_data, _error, variables) => {
      const tools = new Set(variables.actions.map((action) => action.toolName))
      const has = (...names: string[]) => names.some((name) => tools.has(name))
      const keys = new Set<string>()
      const refresh = (...roots: string[]) => roots.forEach((root) => keys.add(root))
      // 'contact-history' feeds « Historique avec X » on a contact's notes.
      if (has('createNote', 'updateNote', 'deleteNote')) refresh('notes', 'note', 'contact-history')
      if (has('createCategory')) refresh('categories')
      if (has('createHashtag')) refresh('hashtags')
      if (has('createGhostProfile', 'updateGhostProfile', 'deleteGhostProfile')) {
        refresh('contacts', 'profile', 'birthdays')
      }
      // A deleted card also leaves every list naming the person, and its notes
      // go with it: the same lists as useDeleteGhostProfile.
      if (has('deleteGhostProfile')) {
        refresh(
          'notes',
          'keep-contacts',
          'categories',
          'hashtags',
          'category-contacts',
          'hashtag-contacts',
          'color-label-contacts',
          'notifications',
        )
      }
      keys.forEach((key) => {
        void queryClient.invalidateQueries({ queryKey: [key] })
      })
    },
  })
}
